import { legToDispatchLeg, legToDispatchLegOptimistic } from '@api/Trinium/maps';
import { triniumApi } from '../../slice';
import { dispatchSlice } from '../Dispatch/slice';
import { upsertLegInDispatchCache } from '../Dispatch/types';
import { Leg, transformLegResponse, updateLegApiCache } from '../Leg';
import {
	Consignment,
	ItemLocationHistory,
	PatchConsignmentRequest,
	PostConsignmentRequest,
	PutConsignmentRequest,
	PutConsignmentsRequest,
	SendFinalChargesRequest,
	SendRailBillingRequest,
	transformConsignmentResponse,
	transformConsignmentsResponse,
	transformItemLocationHistoryResponse
} from './types';
import { AddChargePutRequest, ChargeResponseRaw, updateChargesAndPaymentsCache } from '../ChargesAndPayments/types';
import { chargesAndPaymentsSlice } from '../ChargesAndPayments/slice';

/**
 * Fixed Cache Key to reference the same mutation hooks for the Leg Drawer component where
 * individual legs can be added/removed and should wait until resolved to allow another request.
 */
export const LegDrawerFixedCacheKey = 'leg-drawer-mutation';

export const ConsignmentFixedCacheKey = 'consignment-mutation';

/**
 * Fixed Cache Key so the in-flight state of a final charges send survives remounts
 * (the mobile toolbar menu unmounts its buttons on close), preventing duplicate sends.
 */
export const SendFinalChargesFixedCacheKey = 'send-final-charges-mutation';

export const consignmentSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchConsignment: builder.query<Consignment, { terminalNumber: string; consignmentId: number }>({
				query: ({ terminalNumber, consignmentId }) => ({
					url: `/${terminalNumber}/consignments/${String(consignmentId)}`
				}),
				providesTags: (result) => (result ? [{ type: 'consignment', id: result.id }] : []),
				transformResponse: transformConsignmentResponse
			}),

			fetchConsignmentActiveLeg: builder.query<Leg, { terminalNumber: string; consignmentId: number }>({
				query: ({ terminalNumber, consignmentId }) => ({
					url: `/${terminalNumber}/consignments/${String(consignmentId)}/activeLeg`
				}),
				transformResponse: transformLegResponse
			}),

			fetchItemLocationHistory: builder.query<
				ItemLocationHistory[],
				{
					terminalNumber: string;
					consignmentId: number;
					legId?: number;
				}
			>({
				query: ({ terminalNumber, consignmentId }) => ({
					url: `/${terminalNumber}/consignments/${String(consignmentId)}/itemLocHist`
				}),
				transformResponse: transformItemLocationHistoryResponse
			}),

			postConsignment: builder.mutation<Consignment[], PostConsignmentRequest>({
				query: ({ terminalNumber, ...consignment }) => ({
					url: `/${terminalNumber}/consignments`,
					method: 'POST',
					body: { ...consignment }
				}),
				transformResponse: transformConsignmentsResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;

					result.data.forEach((consignment) => {
						/** Use upsertQueryData to replace the Consignment Cache entry with the response */
						dispatch(
							consignmentSlice.util.upsertQueryData(
								'fetchConsignment',
								{
									terminalNumber: params.terminalNumber,
									consignmentId: consignment.id
								},
								consignment
							)
						).catch(() => {
							dispatch(consignmentSlice.util.invalidateTags(['consignment']));
						});

						/** Update the Dispatch and Leg APIs with the newest Leg information returned */
						consignment.consignmentLegs.forEach((leg: Leg) => {
							upsertLegInDispatchCache(legToDispatchLeg(leg), { isNewLeg: true });
						});
					});
				}
			}),

			putConsignment: builder.mutation<Consignment, PutConsignmentRequest>({
				query: ({ terminalNumber, id, actionType }) => ({
					url: `/${terminalNumber}/consignments/${String(id)}`,
					method: 'PUT',
					body: {
						action: actionType.action,
						...('data' in actionType ? { data: actionType.data } : {})
					}
				}),
				transformResponse: transformConsignmentResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;

					/** Use upsertQueryData to replace the Consignment Cache entry with the response */
					await dispatch(
						consignmentSlice.util.upsertQueryData(
							'fetchConsignment',
							{
								terminalNumber: params.terminalNumber,
								consignmentId: params.id
							},
							result.data
						)
					);

					/** Update the Dispatch and Leg APIs with the newest Leg information returned */
					result.data.consignmentLegs.forEach((leg: Leg) => {
						upsertLegInDispatchCache(legToDispatchLeg(leg));
						updateLegApiCache(leg);
					});

					/** When approving charges, refresh the charges and payments cache to reflect updated on-hold status */
					if (params.actionType.action === 'approve') {
						dispatch(
							chargesAndPaymentsSlice.util.invalidateTags([
								{
									type: 'charges',
									id: params.id
								},
								{
									type: 'payments',
									id: params.id
								}
							])
						);
					}
				}
			}),

			putConsignments: builder.mutation<Consignment[], PutConsignmentsRequest>({
				query: ({ terminalNumber, actionType: { action, data } }) => ({
					url: `/${terminalNumber}/consignments`,
					method: 'PUT',
					body: { action: action, data: { ...data } }
				}),
				transformResponse: transformConsignmentsResponse,
				async onQueryStarted(_params, { queryFulfilled }) {
					const result = await queryFulfilled;

					// Update the cache with the newly retrieved Consignment Leg data
					result.data.forEach((consignment: Consignment) => {
						// At this time, the Consignment cache does not need to be updated
						// Update each associated Consignment Leg data
						consignment.consignmentLegs.forEach((leg: Leg) => {
							upsertLegInDispatchCache(legToDispatchLeg(leg));
							updateLegApiCache(leg);
						});
					});
				}
			}),

			patchConsignment: builder.mutation<Consignment, PatchConsignmentRequest>({
				query: ({ terminalNumber, consignment: { id, ...payload } }) => ({
					url: `${terminalNumber}/consignments/${String(id)}`,
					method: 'PATCH',
					body: { ...payload }
				}),
				transformResponse: transformConsignmentResponse,
				async onQueryStarted({ terminalNumber, consignment: { id, ...payload }, ...params }, { queryFulfilled, dispatch }) {
					let optimisticUpdates = undefined;
					if (params.optimistic && params.leg) {
						optimisticUpdates = upsertLegInDispatchCache(legToDispatchLegOptimistic(params.leg));
					}

					try {
						const result = await queryFulfilled;

						if (params.skipCacheUpdate) return;

						dispatch(
							consignmentSlice.util.updateQueryData(
								'fetchConsignment',
								{
									terminalNumber: terminalNumber,
									consignmentId: id
								},
								(draft) => {
									Object.assign(draft, {
										/** Apply the fields sent up to the cache in case they do not return if they were null / empty / default value */
										...payload,
										...result.data
									});
								}
							)
						);

						/**
						 * The Leg payload carries no consignment notes, so the board's Consignment Notes column has to be
						 * stamped from the Consignment. Guarded on the request because any other patch would otherwise blank it.
						 */
						const csnNotes = payload.notes !== undefined ? (result.data.notes ?? payload.notes ?? '') : undefined;

						/** Update the Dispatch and Leg APIs with the newest Leg information returned */
						result.data.consignmentLegs.forEach((leg: Leg) => {
							const dispatchLeg = legToDispatchLeg(leg);
							if (csnNotes !== undefined) {
								dispatchLeg.csnNotes = csnNotes;
							}

							upsertLegInDispatchCache(dispatchLeg);
							updateLegApiCache(leg);
						});
					} catch {
						// Rollback the optimistic update and forward on the error
						optimisticUpdates?.forEach((currentOptimisticUpdate) => {
							currentOptimisticUpdate.undo();
						});
					}
				}
			}),

			// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
			sendRailBilling: builder.mutation<void, SendRailBillingRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/consignments/${String(params.consignmentId)}`,
					method: 'PUT',
					body: {
						action: 'emailRailBilled',
						data: {
							addr: params.emailAddr,
							subject: params.emailSubject,
							body: params.emailBody
						}
					}
				})
			}),

			sendFinalCharges: builder.mutation<null, SendFinalChargesRequest>({
				query: (params) => ({
					url: `${params.terminalNumber}/consignments/${String(params.consignmentId)}`,
					method: 'PUT',
					body: {
						action: 'sendFinalCharges',
						data: {
							subject: params.emailSubject,
							body: params.emailBody
						}
					}
				})
			}),

			addCharge: builder.mutation<ChargeResponseRaw, AddChargePutRequest>({
				query: ({ terminalNumber, csnId, charge, payment, asCopy }) => ({
					url: `${terminalNumber}/consignments/${String(csnId)}`,
					method: 'PUT',
					body: {
						action: 'addCharge',
						data: {
							charge: { ...charge },
							payment: payment ? { ...payment } : undefined,
							asCopy: asCopy || undefined
						}
					}
				}),
				async onQueryStarted(params, { queryFulfilled }) {
					const result = await queryFulfilled;

					await updateChargesAndPaymentsCache(params.csnId, result.data);
				}
			}),

			cancelConsignment: builder.mutation<
				Consignment,
				{
					terminalNumber: string;
					consignmentId: number;
					cancelType: 'consignment' | 'job';
					jobId?: number;
					cancelReason: string;
				}
			>({
				query: ({ terminalNumber, consignmentId, cancelType, jobId, cancelReason }) => ({
					url: `${terminalNumber}/consignments/${String(consignmentId)}`,
					method: 'PUT',
					body: {
						action: 'cancel',
						data: cancelType === 'job' ? { jobId, cancelReason } : { csnId: consignmentId, cancelReason }
					}
				}),
				transformResponse: transformConsignmentResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;

					/**
					 * Remove cancelled legs from dispatch caches.
					 * This must happen BEFORE any tag invalidation for grid to update after modal closes.
					 *
					 * For job cancellation: filter out all legs matching the jobId
					 * For consignment cancellation: filter out all legs matching the consignmentId
					 */
					const jobId = params.jobId ?? result.data.jobId;

					dispatch(
						dispatchSlice.util.updateQueryData('fetchDispatchLegs', params.terminalNumber, (draft) => {
							if (params.cancelType === 'job') {
								return draft.filter((leg) => leg.jobId !== params.jobId);
							} else {
								return draft.filter((leg) => leg.csnId !== params.consignmentId);
							}
						})
					);

					dispatch(
						dispatchSlice.util.updateQueryData(
							'fetchDispatchLegsByJobId',
							{
								terminalNumber: params.terminalNumber,
								jobId: String(jobId)
							},
							(draft) => {
								if (params.cancelType === 'job') {
									return draft.filter((leg) => leg.jobId !== params.jobId);
								} else {
									return draft.filter((leg) => leg.csnId !== params.consignmentId);
								}
							}
						)
					);

					/** Update the Consignment Cache with the cancelled consignment */
					await dispatch(
						consignmentSlice.util.upsertQueryData(
							'fetchConsignment',
							{
								terminalNumber: params.terminalNumber,
								consignmentId: params.consignmentId
							},
							result.data
						)
					);

					result.data.consignmentLegs.forEach((leg: Leg) => {
						updateLegApiCache(leg);
					});

					/**
					 * The Advanced Leg Search caches its own results (fetchDispatchLegsBySearch) and is not touched by
					 * the leg-removal above. Invalidate it so it refetches — the cancelled consignment then returns as a
					 * single no-leg "Cancelled" row instead of continuing to show its now-deleted active leg.
					 */
					dispatch(dispatchSlice.util.invalidateTags([{ type: 'dispatch-by-search', id: 'LIST' }]));
				}
			}),

			validateCustRefNo: builder.query<{ message: string }, { terminalNumber: string; custId: string; customerRef: string }>({
				query: ({ terminalNumber, custId, customerRef }) => ({
					url: `/${terminalNumber}/consignments/validateCustRefNo?custId=${encodeURIComponent(custId)}&custRefNo=${encodeURIComponent(customerRef)}`
				})
			})
		};
	}
});

export const {
	useFetchConsignmentQuery,
	useLazyFetchConsignmentActiveLegQuery,
	useFetchItemLocationHistoryQuery,
	usePostConsignmentMutation,
	usePatchConsignmentMutation,
	usePutConsignmentMutation,
	usePutConsignmentsMutation,
	useSendRailBillingMutation,
	useSendFinalChargesMutation,
	useAddChargeMutation,
	useCancelConsignmentMutation
} = consignmentSlice;
